"use client";
import NextLink from "next/link";
import { signIn as nextAuthSignIn } from "next-auth/react";

import { Link } from "@heroui/link";

import { PageFormTemplate } from "../components/PageFormTemplate";

import { FormInput } from "@/components/forms/components/FormInputs";
import { signInAction } from "@/lib/actions/auth";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";
import { LoginData, loginSchema } from "@/lib/validations/auth";

interface LoginFormProps {
  callbackUrl: string;
}

export function LoginForm({ callbackUrl }: LoginFormProps) {
  // Default values for the form
  const defaultValues: LoginData = {
    email: "",
    password: "",
  };

  // Form submission handler
  const handleFormSubmit = async (data: LoginData) => {
    // First validate credentials with server action
    const validationResult = await signInAction(data);

    if (!validationResult.success) {
      return validationResult;
    }

    // If validation succeeds, create session with NextAuth
    const signInResult = await nextAuthSignIn("credentials", {
      callbackUrl,
      email: data.email,
      password: data.password,
      redirect: false,
    });

    if (signInResult?.error) {
      return {
        error: "Invalid credentials. Please try again.",
        success: false,
      };
    } else {
      return { message: "Login successful", success: true };
    }
  };

  return (
    <PageFormTemplate
      callbackUrl={callbackUrl}
      defaultValues={defaultValues}
      description="Enter your credentials to continue"
      eventKeys={{
        attempt: EventKeys.AUTH_LOGIN_FORM_SUBMIT_ATTEMPT,
        failure: EventKeys.AUTH_LOGIN_FORM_SUBMIT_FAILURE,
        success: EventKeys.AUTH_LOGIN_FORM_SUBMIT_SUCCESS,
      }}
      formSchema={loginSchema}
      icon="LogIn"
      submitLabel="Log In"
      title="Login to NeedBridge"
      onSubmit={handleFormSubmit}
    >
      {({ control }) => (
        <>
          <FormInput
            required
            control={control}
            label="Email"
            name="email"
            placeholder="Enter your email"
            type="email"
          />

          <FormInput
            required
            control={control}
            label="Password"
            name="password"
            placeholder="Enter your password"
            type="password"
          />

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">How to join NeedBridge:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Organization administrators can{" "}
                <Link
                  as={NextLink}
                  className="text-primary font-medium"
                  href={routes.auth.register()}
                >
                  register here
                </Link>{" "}
                to create a new organization
              </li>
              <li>
                Case workers should contact their organization for a
                registration link
              </li>
              <li>
                Area coordinators must be invited by an organization
                administrator
              </li>
              <li>
                Check your email for an invitation or contact your organization
                admin
              </li>
            </ul>
          </div>

          <div className="flex justify-between text-sm">
            <div className="space-y-1">
              <Link
                className="text-primary block"
                href={routes.auth.forgotPassword()}
              >
                Forgot password?
              </Link>
            </div>
            <NextLink className="text-primary" href={routes.auth.register()}>
              Register organization
            </NextLink>
          </div>
        </>
      )}
    </PageFormTemplate>
  );
}
